import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { CButton, CCol, CFormInput, CFormSelect, CRow } from '@coreui/react-pro'
import {
  getAdmissionId,
  getAdmissionDetailsById,
  getStudentId,
  getStudentRollNO,
  getStudentSequenceNo,
  getStudentConfiguration,
  checkRollNoOfStudent,
} from 'src/services/student/student-services'
import { getClusterDetails } from 'src/services/transport/transport-services'
import { getAllClusters } from 'src/services/student/admission-services'
import Swal from 'sweetalert2'

const ConfirmAdmission = ({ confirmAdmissionId, onEnroll }) => {
  const [studentData, setStudentData] = useState(null)
  const [allClusters, setAllClusters] = useState([])
  const [feeHeads, setFeeHeads] = useState([])
  const [monthlyTotal, setMonthlyTotal] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const [studConfig, setStudConfig] = useState({
    SEQUENCE_NO_INPUT: false,
    STUDENT_ID_INPUT: false,
  })

  const [formValues, setFormValues] = useState({
    feeGroup: '',
    rollNo: '',
    sequenceNo: '',
    studentId: '',
    admissionId: '',
    classId: '',
    sectionId: '',
    nextSession: '',
    studentType: '',
  })

  useEffect(() => {
    if (!confirmAdmissionId) return

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const res = await getAdmissionDetailsById(confirmAdmissionId)
        if (res.data?.data?.length > 0) {
          const student = res.data.data[0]
          setStudentData(student)

          const updatedForm = {
            studentId: student.Student_Details_Id,
            admissionId: student.Admission_Id,
            classId: student.Class_Id,
            sectionId: student.Class_Sec_Id,
            nextSession: student.Session,
            studentType: student.Student_Type || 'NEW',
            feeGroup: '',
            rollNo: '',
            sequenceNo: '',
          }
          setFormValues(updatedForm)

          const clusterRes = await getAllClusters(student.Class_Id, student.Session, 'NEW')
          const clusters = clusterRes.data || []
          setAllClusters(clusters)

          if (clusters.length > 0) {
            const firstCluster = clusters[0]
            setFormValues((prev) => ({
              ...prev,
              feeGroup: firstCluster.FG_Id,
            }))
          }

          const rollRes = await getStudentRollNO(
            student.Class_Id,
            student.Class_Sec_Id,
            student.Session,
          )
          const rollData = rollRes?.data
          const rollNo = rollData?.Roll_No_AutoCount ? rollData.Roll_No : ''

          const seqRes = await getStudentSequenceNo(student.Class_Id, student.Session)
          const sequenceNo = seqRes?.data?.data || ''

          const studentIdRes = await getStudentId(student.Class_Id, student.Session, sequenceNo)
          const studentId = studentIdRes?.data?.data || ''

          const admissionIdRes = await getAdmissionId(student.Class_Id, student.Session, sequenceNo)
          const admissionId = admissionIdRes?.data?.data || ''

          const configRes = await getStudentConfiguration()
          const configData = configRes?.data?.data || {}
          const admissionConfig = configData?.ADMISSION || {}
          setStudConfig({
            SEQUENCE_NO_INPUT: admissionConfig?.SEQUENCE_NO_INPUT?.V === '1',
            STUDENT_ID_INPUT: admissionConfig?.STUDENT_ID_INPUT?.V === '1',
          })
          console.log('studConfig', admissionConfig)
          setFormValues((prev) => ({
            ...prev,
            rollNo,
            sequenceNo,
            studentId,
            admissionId,
          }))
        }
      } catch (err) {
        console.error('Error fetching student/clusters:', err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [confirmAdmissionId])

  useEffect(() => {
    if (!formValues.feeGroup) return

    getClusterDetails(formValues.feeGroup, formValues.nextSession, 'Regular')
      .then((response) => {
        setFeeHeads(response.data.data.fee_heads || [])
        setMonthlyTotal(response.data.data.monthly_totals || {})
      })
      .catch((error) => console.error(error))
  }, [formValues.feeGroup, formValues.nextSession])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckRollNo = async () => {
    if (
      !formValues.rollNo ||
      !formValues.classId ||
      !formValues.sectionId ||
      !formValues.nextSession
    ) {
      Swal.fire({
        icon: 'warning',
        text: 'Please fill Class, Section, Session, and Roll No.',
        timer: 2000,
        showConfirmButton: false,
      })
      return
    }

    try {
      const res = await checkRollNoOfStudent(
        formValues.classId,
        formValues.sectionId,
        formValues.nextSession,
        formValues.rollNo,
      )

      Swal.fire({
        icon: res?.data?.status === 200 ? 'success' : 'warning',
        text: res?.data?.Message || 'No response',
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error('Error checking roll no:', err)
    }
  }

  return (
    <>
      {studentData && (
        <div className="table-responsive mb-4">
          <table className="table table-bordered">
            <tbody>
              <tr>
                <th style={{ width: '20%' }}>Name</th>
                <td style={{ width: '30%' }}>
                  {studentData.First_Name} {studentData.Middle_Name} {studentData.Last_Name}
                </td>
                <th style={{ width: '20%' }}>Student Id</th>
                <td style={{ width: '30%' }}>{confirmAdmissionId}</td>
              </tr>
              <tr>
                <th>Class</th>
                <td>{studentData.Class_Name}</td>
                <th>Stream</th>
                <td>{studentData.Stream}</td>
              </tr>
              <tr>
                <th>Father's Name</th>
                <td>{studentData.Father_Name}</td>
                <th>Mother's Name</th>
                <td>{studentData.Mother_Name}</td>
              </tr>
              <tr>
                <th>DOB</th>
                <td>{studentData.DOB}</td>
                <th>Discount Category</th>
                <td>{studentData.Concession_Name}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <CRow className="d-flex justify-content-center">
        <CCol lg={3} md={3} xl={3} className="mt-2">
          <label className="form-label">Select Fee Group To Apply</label>
          <CFormSelect name="feeGroup" value={formValues.feeGroup} onChange={handleChange}>
            <option value="">--Select--</option>
            {allClusters.map((data, index) => (
              <option key={index} value={data.FG_Id}>
                {data.FG_Name}
              </option>
            ))}
          </CFormSelect>
        </CCol>
      </CRow>

      {feeHeads.length > 0 && (
        <>
          <CRow className="mt-4 text-center">
            <p>Monthwise Fee Details</p>
          </CRow>
          <CRow>
            <CCol className="mt-4">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>Fee Head</th>
                    {feeHeads[0].amounts &&
                      Object.keys(feeHeads[0].amounts).map((month, idx) => (
                        <th key={idx}>{month}</th>
                      ))}
                    <th>Total Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {feeHeads.map((head, index) => (
                    <tr key={index}>
                      <td>{head.name}</td>
                      {head.amounts ? (
                        Object.keys(head.amounts).map((month, idx) => (
                          <td key={idx}>{head.amounts[month]}</td>
                        ))
                      ) : (
                        <td colSpan={Object.keys(feeHeads[0]?.amounts || {}).length}>No data</td>
                      )}
                      <td>{head.total || 0}</td>
                    </tr>
                  ))}

                  {monthlyTotal && Object.keys(monthlyTotal).length > 0 && (
                    <tr className="fw-bold">
                      <td>Total</td>
                      {Object.keys(monthlyTotal).map((month, idx) => (
                        <td key={idx}>{monthlyTotal[month]}</td>
                      ))}
                      <td>{Object.values(monthlyTotal).reduce((a, b) => a + Number(b || 0), 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CCol>
          </CRow>
        </>
      )}

      <CRow className="mt-4">
        <CCol lg={3}>
          <CFormInput
            label="Id Sequence No."
            value={formValues.sequenceNo}
            name="sequenceNo"
            onChange={handleChange}
            readOnly={!studConfig.SEQUENCE_NO_INPUT}
          />
        </CCol>

        <CCol lg={2}>
          <CFormInput
            label="Student Id"
            value={formValues.studentId}
            name="studentId"
            readOnly={!studConfig.STUDENT_ID_INPUT}
          />
        </CCol>
        <CCol lg={2}>
          <CFormInput label="Admission Id" value={formValues.admissionId} name="admissionId" />
        </CCol>
        <CCol lg={3}>
          <CFormInput
            label="Roll No."
            value={formValues.rollNo}
            name="rollNo"
            onChange={handleChange}
          />
        </CCol>
        <CCol lg={2} className="d-flex justify-content-end align-items-end">
          <CButton color="warning" className="text-white" onClick={handleCheckRollNo}>
            Check Roll No.
          </CButton>
        </CCol>
      </CRow>

      <CRow className="d-flex justify-content-center mt-4">
        <CCol lg={3} className="d-flex justify-content-center">
          <CButton color="success" className="text-white" onClick={() => onEnroll(formValues)}>
            Enroll Student
          </CButton>
        </CCol>
      </CRow>
    </>
  )
}

ConfirmAdmission.propTypes = {
  confirmAdmissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEnroll: PropTypes.func.isRequired,
}

export default ConfirmAdmission
